/**
 * Luvè Corner Trio (snippets/luve-corner-trio.liquid): pick a colour, see the three trees in it, add all three to the
 * cart in one go. The add follows Horizon's own batch add in assets/product-form.js, so the cart drawer opens and the
 * cart count updates exactly as for any other add. On a product page it also follows the colour picker.
 */
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ADDED_MS = 1600;

/** A$ always, no cents on whole dollars: A$1,097, A$19.95 */
const money = (cents) =>
  `A$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * @typedef {{ id: number, size: string, cm: number, price: number }} TrioItem
 * @typedef {{ name: string, tones: string[], total: number, saving?: number, available: boolean, items: TrioItem[] }} TrioColour
 */

class LuveCornerTrio extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {TrioColour[]} */
  #colours = [];
  #colour = '';
  #busy = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimer;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.#colours = JSON.parse(this.querySelector('[data-trio-data]')?.textContent || '[]');
    } catch {
      this.#colours = [];
    }

    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    this.#colour = checked?.value || this.#colours[0]?.name || '';

    this.addEventListener('change', this.#onChange, { signal });
    this.#button?.addEventListener('click', this.#onAdd, { signal });

    if (this.hasAttribute('data-follow-picker')) {
      document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { capture: true, signal });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#addedTimer);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-trio-add]'));
  }

  get #label() {
    return this.#button?.dataset.label || 'Add the trio';
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'radio') this.#select(input.value);
  };

  /** @param {Event & { selectedOptions?: Array<{ name: string, value: string }> }} event */
  #onProductSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || target.closest('product-card')) return;

    const scope = this.closest('.shopify-section, dialog');
    if (!scope || target.closest('.shopify-section, dialog') !== scope) return;

    const picked = (event.selectedOptions || []).find((option) => option?.name === this.dataset.colourOption);
    if (picked) this.#select(picked.value);
  };

  /** @param {string} colour */
  #select(colour) {
    const entry = this.#colours.find((candidate) => candidate.name === colour);
    if (!entry) return;
    this.#colour = colour;

    const [base, highlight, shade, line] = entry.tones;
    this.style.setProperty('--lt-tree-base', base);
    this.style.setProperty('--lt-tree-hi', highlight);
    this.style.setProperty('--lt-tree-shade', shade);
    this.style.setProperty('--lt-tree-line', line);

    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = radio.value === colour;
    }

    const colourLabel = this.querySelector('[data-trio-colour]');
    if (colourLabel) colourLabel.textContent = `in ${colour}`;

    // The bundle saving (Theme settings > Luvè bundle savings) comes off the plain sum, as at checkout
    const saving = entry.saving || 0;
    const due = entry.total - saving;
    const price = this.querySelector('[data-trio-price]');
    if (price) price.textContent = money(due);
    const was = /** @type {HTMLElement | null} */ (this.querySelector('[data-trio-was]'));
    if (was) {
      was.textContent = money(entry.total);
      was.hidden = saving === 0;
    }
    const saved = this.querySelector('[data-trio-saved]');
    if (saved) saved.textContent = money(saving);

    const free = /** @type {HTMLElement | null} */ (this.querySelector('.luve-trio__free'));
    const threshold = Number(this.dataset.threshold) || 0;
    if (free) free.hidden = !(threshold > 0 && due >= threshold);

    clearTimeout(this.#addedTimer);
    this.#setButton(entry.available ? this.#label : 'Not available yet', !entry.available);
    this.#setStatus('');
  }

  #onAdd = () => {
    const entry = this.#colours.find((candidate) => candidate.name === this.#colour);
    if (!entry || !entry.available || this.#busy) return;

    this.#busy = true;
    this.#setButton('Adding', true);
    this.#setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const itemCount = entry.items.length;
    const detail = { source: 'luve-corner-trio', sourceId: this.dataset.uid || '', itemCount, productId: this.dataset.productId };
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: entry.items.map((item) => ({ merchandiseId: String(item.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: entry.items.map((item) => ({ id: item.id, quantity: 1 })),
        sections: sectionIds.join(','),
      }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const cart = await this.#fetchCart();

        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { ...detail, items: cart.items, didError: true },
          });
          this.#setStatus(response.description || response.message || 'The trio could not be added.', true);
          this.#setButton(this.#label, false);
          return;
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, sections: response.sections, didError: false },
        });
        this.#setButton('Added', true);
        this.#setStatus(`The Corner Trio in ${entry.name} is in your cart.`);
        this.#addedTimer = setTimeout(() => this.#setButton(this.#label, false), ADDED_MS);
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
        );
        this.#setStatus('Something went wrong. Please try again.', true);
        this.#setButton(this.#label, false);
      })
      .finally(() => {
        this.#busy = false;
      });
  };

  /** The cart after the add: through Horizon's cart component when the page has one, otherwise /cart.js */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      // @ts-ignore Horizon's CartItemsComponent
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /**
   * @param {string} text
   * @param {boolean} disabled
   */
  #setButton(text, disabled) {
    const button = this.#button;
    if (!button) return;
    button.textContent = text;
    button.disabled = disabled;
  }

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #setStatus(text, isError = false) {
    const status = this.querySelector('[data-trio-status]');
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('luve-corner-trio')) {
  customElements.define('luve-corner-trio', LuveCornerTrio);
}
